import {
  Alert,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { Value } from 'features/plc/components/common';
import {
  getPathName,
  PLC_PATHS,
} from 'features/plc/contracts/pressHMI.schema';
import { usePLCValues } from 'features/plc/providers/PLCProvider';
import React from 'react';

const toRtdCelsius = (value) => {
  if (typeof value !== 'number') {
    return null;
  }

  return value / 10;
};

const formatTemp = (value) => {
  if (typeof value !== 'number') {
    return '--';
  }

  return `${value.toFixed(2).replace(/\.?0+$/, '')} °C`;
};

const formatRtd = (value) => formatTemp(toRtdCelsius(value));

const isNumber = (value) => typeof value === 'number';

const getRtdLimitStatus = ({ field, rawValue, config }) => {
  const celsius = toRtdCelsius(rawValue);

  if (!isNumber(celsius)) {
    return false;
  }

  if (field.kind === 'tool') {
    return (isNumber(config.toolTempMinValid) && celsius < config.toolTempMinValid)
      || (isNumber(config.toolTempMax) && celsius > config.toolTempMax);
  }

  return isNumber(config.heaterTempMax) && celsius > config.heaterTempMax;
};

const renderRtdValue = ({ field, rawValue, config }) => {
  const displayValue = formatRtd(rawValue);

  if (!getRtdLimitStatus({ field, rawValue, config })) {
    return displayValue;
  }

  return (
    <StatusIndicator type="error">
      {displayValue}
    </StatusIndicator>
  );
};

const getSectionMessages = ({ section, values, config }) => {
  const toolTemps = section.fields
    .filter((field) => field.kind === 'tool')
    .map((field) => toRtdCelsius(values[getPathName(field.path)]))
    .filter(isNumber);
  const messages = [];

  if (
    toolTemps.some((temp) => (
      (isNumber(config.toolTempMinValid) && temp < config.toolTempMinValid)
      || (isNumber(config.toolTempMax) && temp > config.toolTempMax)
    ))
  ) {
    messages.push(
      `Tool RTD outside configured valid range (${formatTemp(config.toolTempMinValid)} to ${formatTemp(config.toolTempMax)}).`,
    );
  }

  if (toolTemps.length > 1 && isNumber(config.toolRtdSpreadMax)) {
    const spread = Math.max(...toolTemps) - Math.min(...toolTemps);

    if (spread > config.toolRtdSpreadMax) {
      messages.push(`Tool RTD spread is ${formatTemp(spread)}; limit is ${formatTemp(config.toolRtdSpreadMax)}.`);
    }
  }

  const heaterField = section.fields.find((field) => field.kind === 'heater');
  const heaterTemp = heaterField ? toRtdCelsius(values[getPathName(heaterField.path)]) : null;

  if (isNumber(heaterTemp) && isNumber(config.heaterTempMax) && heaterTemp > config.heaterTempMax) {
    messages.push(`Heater safety RTD is above ${formatTemp(config.heaterTempMax)}.`);
  }

  return messages;
};

const UPPER_THERMAL_FIELDS = [
  { label: 'Upper Tool RTD 1', path: PLC_PATHS.status.pressInputs.upperToolRtd1, kind: 'tool' },
  { label: 'Upper Tool RTD 2', path: PLC_PATHS.status.pressInputs.upperToolRtd2, kind: 'tool' },
  { label: 'Upper Tool RTD 3', path: PLC_PATHS.status.pressInputs.upperToolRtd3, kind: 'tool' },
  { label: 'Upper PEEK RTD', path: PLC_PATHS.status.pressInputs.upperPeekRtd, kind: 'heater' },
];

const LOWER_THERMAL_FIELDS = [
  { label: 'Lower Tool RTD 1', path: PLC_PATHS.status.pressInputs.lowerToolRtd1, kind: 'tool' },
  { label: 'Lower Tool RTD 2', path: PLC_PATHS.status.pressInputs.lowerToolRtd2, kind: 'tool' },
  { label: 'Lower Tool RTD 3', path: PLC_PATHS.status.pressInputs.lowerToolRtd3, kind: 'tool' },
  { label: 'Lower Heater RTD', path: PLC_PATHS.status.pressInputs.lowerHeaterRtd, kind: 'heater' },
];

const THERMAL_SECTIONS = [
  {
    title: 'Upper Thermals',
    fields: UPPER_THERMAL_FIELDS,
    configPaths: PLC_PATHS.cmd.upperHeatZoneConfig,
  },
  {
    title: 'Lower Thermals',
    fields: LOWER_THERMAL_FIELDS,
    configPaths: PLC_PATHS.cmd.lowerHeatZoneConfig,
  },
];

const ALL_THERMAL_PATHS = THERMAL_SECTIONS.flatMap(
  (section) => [
    ...section.fields.map((field) => field.path),
    section.configPaths.toolTempMinValid,
    section.configPaths.toolTempMax,
    section.configPaths.toolRtdSpreadMax,
    section.configPaths.heaterTempMax,
  ],
);

export default function HeaterThermalStatus() {
  const values = usePLCValues(ALL_THERMAL_PATHS);

  return (
    <Container header={<Header variant="h2">Heater thermal status</Header>}>
      <ColumnLayout columns={2}>
        {THERMAL_SECTIONS.map((section) => {
          const config = {
            heaterTempMax: values[getPathName(section.configPaths.heaterTempMax)],
            toolRtdSpreadMax: values[getPathName(section.configPaths.toolRtdSpreadMax)],
            toolTempMax: values[getPathName(section.configPaths.toolTempMax)],
            toolTempMinValid: values[getPathName(section.configPaths.toolTempMinValid)],
          };
          const messages = getSectionMessages({ section, values, config });

          return (
            <SpaceBetween size="xs" key={section.title}>
              {messages.length ? (
                <Alert type="error" header={section.title}>
                  <SpaceBetween size="xxs">
                    {messages.map((message) => (
                      <div key={message}>{message}</div>
                    ))}
                  </SpaceBetween>
                </Alert>
              ) : null}
              {section.fields.map((field) => {
                const value = values[getPathName(field.path)];

                return (
                  <Value
                    key={getPathName(field.path)}
                    label={field.label}
                    value={renderRtdValue({ field, rawValue: value, config })}
                  />
                );
              })}
            </SpaceBetween>
          );
        })}
      </ColumnLayout>
    </Container>
  );
}
